<template>
  <main class="relative bg-black pb-16 pt-30">
    <div class="relative px-8">
      <template v-for="(eSpot, key) in eSpots" :key>
        <cms-section v-if="eSpot" :section="eSpot" />
      </template>
    </div>

    <div
      class="relative mx-a overflow-hidden text-center"
      style="max-width: 45rem"
    >
      <div class="grid cols-2 my-16 gap-6 px-10 c-white">
        <div v-for="({ icon, text }, key) in getFilteredBenefits" :key class="md:px-8">
          <img class="mx-a mb-3 h-12" v-bind="icon" role="presentation">

          <p v-html="text" class="mb-2 text-sm lh-tight" />
        </div>
      </div>

      <vf-notification
        v-if="notification.type"
        v-style:w="{ sm: '90%', md: 'initial' }"
        class="mx-a mt-12"
        dismissible
        :type="notification.type"
        @close="dismissNotification"
      >
        {{ notification.message }}
      </vf-notification>

      <base-form
        v-if="!isSubmissionSuccessful"
        ref="formRef"
        data-test-id="form-newsletter"
        :form
        name="newsletter"
      >
        <div class="mx-a max-w-100 px-10 text-left">
          <p class="mx-a text-right text-3 c-white lh-tight op-50">
            {{ requiredFields }}
          </p>

          <vf-form-field
            v-slot="{ attrs }"
            name="firstName"
            :rule="[
              validateName($t.validation.firstName),
            ]"
            variant="inverse"
          >
            <vf-input
              v-model="form.firstName"
              v-bind="attrs"
              class="mt-4"
              :disabled="loading"
              type="text"
              variant="inverse"
            >
              {{ $t.firstName }}
            </vf-input>
          </vf-form-field>

          <vf-form-field
            v-slot="{ attrs }"
            name="lastName"
            :rule="[
              validateName($t.validation.lastName),
            ]"
            variant="inverse"
          >
            <vf-input
              v-bind="attrs"
              v-model="form.lastName"
              class="mt-4"
              :disabled="loading"
              type="text"
              variant="inverse"
            >
              {{ $t.lastName }}
            </vf-input>
          </vf-form-field>

          <vf-form-field
            v-slot="{ attrs }"
            name="email"
            :rule="[validateRequired(), validateEmail()]"
            variant="inverse"
          >
            <vf-input
              v-model="form.email"
              v-bind="attrs"
              class="mt-4"
              :disabled="loading"
              required
              type="email"
              variant="inverse"
            >
              {{ $t.newsletterEmailAddress }}
            </vf-input>
          </vf-form-field>

          <vf-form-field
            v-slot="{ attrs }"
            name="gender"
            :rule="[validateRequired()]"
            variant="inverse"
          >
            <vf-select
              v-model="form.gender"
              v-bind="attrs"
              class="mt-4"
              :options="genderOptions"
              required
            >
              {{ $t.gender }}
            </vf-select>
          </vf-form-field>
        </div>

        <div class="mt-16 px-10 c-white">
          <h2 class="mb-2 title-2 capitalize">
            {{ birthdayHeading }}
          </h2>

          <p v-if="!$feature.enableLoyaltyFeatures">
            {{ birthdaySubheading }}
          </p>
        </div>

        <div class="mx-a mt-6 max-w-100 px-10">
          <vf-form-field
            v-slot="{ attrs }"
            name="birthDate"
            :rule="[
              validateDate('YYYY-MM-DD', $t.dobValidationMsg),
              validateMinDate(minBirthDate, $t.dobValidationMsg),
              validateMaxDate(maxBirthDate, replaceAll($t.dobAgeRestrictionMsg, { minimalAge })),
            ]"
          >
            <input-date
              v-model="form.birthDate"
              v-bind="attrs"
              class="text-left"
              :display-format="dateDisplayFormat"
              :placeholder="dateDisplayFormat.toLowerCase()"
            />
          </vf-form-field>
        </div>

        <div class="mt-16 px-10 c-white">
          <h2 class="mb-2 title-2 capitalize">
            {{ interestsHeading }}
          </h2>

          <p>
            {{ interestsSubheading }}
          </p>
        </div>

        <ul class="mb-16 mt-6 flex gap-4 md:flex-wrap md:justify-center md:gap-2 <md:overflow-x-auto <md:px-4">
          <li
            v-for="({ text, value, video }) in interests"
            :key="value"
            v-style:w="{ sm: '50vw', md: '24%' }"
            class="mb-3 shrink-0"
            @mouseleave="pauseInterestVideo(value)"
            @mouseover="playInterestVideo(value)"
          >
            <base-toggle
              v-model="form.interests"
              class="c-white"
              fit="cover"
              name="interests"
              :value
              @change="form.interests.includes(value) ? pauseInterestVideo(value) : playInterestVideo(value)"
            >
              <vf-video
                v-bind="video"
                :ref="el => appendAndInitInterestVideoRef(el, value)"
                class="mb-2 overflow-hidden rounded-xs"
                class-controls="md:hidden"
                loop
                muted
              >
                <transition name="fade">
                  <div v-if="form.interests.includes(value)" class="absolute-0 b b-white bg-black/60" />
                </transition>
              </vf-video>

              <div class="flex center gap-1 subtitle-4">
                <vf-icon v-if="form.interests.includes(value)" name="close" size="sm" />

                <span class="b-b b-transparent duration" :class="{ 'b-white': form.interests.includes(value) }">
                  {{ text }}
                </span>
              </div>
            </base-toggle>
          </li>
        </ul>

        <div class="mx-a px-10" style="max-width: 39rem">
          <vf-form-field
            v-slot="{ attrs }"
            name="privacyPolicy"
            :rule="[validateRequired($t.validators.requiredCheckbox)]"
            variant="inverse"
          >
            <vf-checkbox
              v-model="form.privacyPolicy"
              class="text-left"
              v-bind="attrs"
              :required="true"
              variant="inverse"
            >
              <span class="text-xs">
                <form-legal-disclaimer :content="disclaimer" />
              </span>
            </vf-checkbox>
          </vf-form-field>

          <vf-button
            v-style="{
              'min-w': { lg: '11.25rem' },
            }"
            class="mt-16 <lg:w-full"
            :disabled="!isFormValid || loading"
            type="submit"
            variant="secondary-inverse"
            @click="submit()"
          >
            {{ buttonLabel }}
          </vf-button>
        </div>
      </base-form>
    </div>
  </main>
</template>

<script lang="ts" setup>
import type { FormLocation } from '#types/gtm'
import type { BaseNotification } from '#types/notification'

definePageMeta({ header: { transparent: true } })

const eSpots = (await useCms().getSections.generic('newsletter-sign-up')).data.value?.content.sections
const { $gtm, $t, $viewport, $feature } = useNuxtApp()
const subscriptions = useSubscriptions()
const { gender, minimalAge: minimalAgeConfig } = useAppConfig().components.form.signUp
const minimalAge: number = minimalAgeConfig[useCountryCode()] || minimalAgeConfig
const { getUserEventData } = useAnalytics()
const { data, error } = await useApi().internal.getStaticPage('newsletter-sign-up')
if (!data.value) throw createError({ ...error.value!, fatal: true })

const {
  benefits,
  birthdayHeading,
  birthdaySubheading,
  disclaimer,
  interestsHeading,
  interestsSubheading,
  interests,
  requiredFields,
  buttonLabel
} = data.value

const loading = ref(false)
const formRef = ref()
const genderOptions = gender?.options.map((item) => ({ value: item.value, label: $t.genderOptions[item.label] })) ?? []
const today = await getServerDate()
const minBirthDate = new Date(DATE_MIN)
const maxBirthDate = new Date(today.getTime())
maxBirthDate.setFullYear(today.getUTCFullYear() - minimalAge)
const dateDisplayFormat = DATE_FORMAT_DD_MM_YYYY
const interestsvideoRefs = ref<Record<string, HTMLVideoElement>>({})
const isRecudedMotion = usePreferredReducedMotion().value === 'reduce'

type interestValues = 'alpine_ice_climbing' | 'skiing|snowboarding' | 'strength_training' | 'hiking' | 'running' | 'street_style' | 'environment_and_sustainability'
interface Form {
  firstName?: string
  lastName?: string
  email: string
  gender: 'M' | 'F' | 'NA' | undefined
  birthDate: string // Also optional but not marked as such to satisfy TS
  interests: interestValues[]
  privacyPolicy: boolean
}
const form = reactive<Form>({
  firstName: '',
  lastName: '',
  email: '',
  gender: undefined,
  birthDate: '',
  privacyPolicy: false,
  interests: []
})
const isFormValid = computed(() => {
  return formRef.value?.fields.email.validated
    && formRef.value?.fields.email.invalid === false
    && formRef.value?.fields.gender.invalid === false
    && formRef.value?.fields.privacyPolicy.validated
    && formRef.value?.fields.privacyPolicy.invalid === false
})

const notification = reactive<BaseNotification>({
  type: '',
  message: ''
})

const getFilteredBenefits = benefits.filter(({ nonLoyaltyOnly }) =>
  !$feature.enableLoyaltyFeatures || !nonLoyaltyOnly)

const dismissNotification = () => {
  notification.type = ''
  notification.message = ''
}
const isSubmissionSuccessful = computed(() => notification.type === 'success')

async function submit() {
  try {
    const { firstName, lastName, email, gender, birthDate, interests } = form
    const formLocation: FormLocation = history.state?.formLocation || 'inline:body:newsletter:none'

    loading.value = true
    notification.type = ''

    $gtm.push('user.onLoadUserData', await getUserEventData(form.email))
    $gtm.push('newsletterForm.onSubmit', formLocation)

    await subscriptions.newsletter({
      optIn: true,
      firstName,
      lastName,
      email,
      ...(birthDate && { birthDate }),
      gender,
      interests: interests.join('|'),
      acquisitionType: 'Signup'
    })

    form.privacyPolicy = false
    form.email = ''

    notification.type = 'success'
    notification.message = $t.newsletterSuccess

    $gtm.push('newsletterForm.onConfirmed', formLocation)
  }
  catch (e) {
    if (e instanceof Error) {
      notification.type = 'error'
      notification.message = e.message
    }
  }
  finally {
    loading.value = false
  }
}

const pauseInterestVideo = (key: interestValues) => {
  interestsvideoRefs.value[key].pause()
}

const playInterestVideo = (key: interestValues) => {
  if (form.interests.includes(key)) return

  interestsvideoRefs.value[key].play()
}

const appendAndInitInterestVideoRef = (el, key: interestValues) => {
  if (!el?.videoRef) return
  if (interestsvideoRefs.value[key]) return

  interestsvideoRefs.value[key] = el.videoRef.el

  if (!$viewport.md && !isRecudedMotion) {
    const isVisible = useElementVisibility(el)

    watch(isVisible, (visible) => {
      if (visible)
        playInterestVideo(key)
      else
        pauseInterestVideo(key)
    })
  }
}

useRouter().afterEach(() => {
  if (history.state?.formLocation)
    delete history.state.formLocation
})
</script>
